import React from "react";
import _get from "lodash/get";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Location } from "@reach/router";
import { themeGet } from "styled-system";
import { BounceLoader } from "react-spinners";
import { mapLocaleToLanguage } from "../Layout";


import TextF6 from "../TextF6";
import StyledLink from "../StyledLink";
import * as routes from "../../constants/routes";

import QuizHeader from "../QuizHeader";
import Box from "../Box";
import { SmallContainer } from "../Grid";

import { FormattedMessage, injectIntl } from "react-intl";

import { currentLocale, currentUserLocale } from "../../services/locale";

import { getUser } from "../../state/account";


import Quiz2 from "moocfi-quizzes";

import {
  MULTIPLE_CHOICE,
  CHECKBOX,
  PRIVACY_AGREEMENT,
  ESSAY,
  PEER_REVIEW,
  PEER_REVIEWS_RECEIVED,
  OPEN,
  SCALE,
  RADIO_MATRIX,
  MULTIPLE_OPEN
} from "../../constants/quiztypes";

import MultipleChoice from "../MultipleChoice";
import RadioMatrix from "../RadioMatrix";
import Essay from "../Essay";
import OpenQuiz from "../OpenQuiz";
import MultipleOpen from "../MultipleOpen";

import WrongLocaleMessage from "../WrongLocaleMessage";

const mapQuizTypeToComponent = {
  [ESSAY]: Essay,
  [MULTIPLE_CHOICE]: MultipleChoice,
  [PEER_REVIEW]: null,
  [PEER_REVIEWS_RECEIVED]: null,
  [CHECKBOX]: null,
  [PRIVACY_AGREEMENT]: null,
  [OPEN]: OpenQuiz,
  [SCALE]: null,
  [RADIO_MATRIX]: RadioMatrix,
  [MULTIPLE_OPEN]: MultipleOpen
};

const LoginWarning = styled.div`
  margin: 0;
  padding-top: 3rem;
  padding-bottom: 3rem;
  background: #e9e9ed;
`;

const Wrapper = styled.div`
  margin-bottom: 6rem;
  margin-top: 6rem;
  .question-panel {
    padding-top: 2.5rem;
    padding-bottom: 2.5rem;
    :nth-child(odd) {
      background: #ebe9ef;
    }
    :last-child {
      padding: 5rem 0;
    }
  }
`;

const LoginWarningBox = styled(Box)`
  flex-direction: column;
  display: flex;
  align-content: center;
  text-align: center;
  @media screen and (min-width: 72em) {
    text-align: left;
    flex-direction: row;
    align-items: top;
    justify-content: space-between;
  }
`;

const ButtonLogin = styled(StyledLink)`
  color: #19ca94;
  border: 2px solid #19ca94;
  display: block;
  font-size: 0.875rem;
  border-radius: 90px;
  padding: 0.75rem 1.5rem;
  font-family: ${themeGet("fonts.TTNormsProBold")};
  margin-top: 1rem;
  margin-left: 0.5rem;
  margin-right: 0.5rem;
  transition: all 0.2s ease;
  :hover {
    color: white;
    background: #19ca94;
    border: 2px solid #19ca94;
  }
  @media screen and (min-width: 38em) {
    font-size: 1rem;
    padding: 1rem 3rem;
    margin-left: 1rem;
    margin-right: 1rem;
  }
  @media screen and (min-width: 72em) {
    margin-top: 0;
  }
  :hover {
  }
`;
const ButtonSignUp = styled(StyledLink)`
  color: white;
  background: #19ca94;
  border: 2px solid #19ca94;
  display: block;
  font-size: 0.875rem;
  border-radius: 90px;
  padding: 0.75rem 1.5rem;
  font-family: ${themeGet("fonts.TTNormsProBold")};
  margin-top: 1rem;
  margin-left: 0.5rem;
  margin-right: 0.5rem;
  transition: all 0.2s ease;
  :hover {
    background: #11ab7c;
    border: 2px solid #11ab7c;
  }
  @media screen and (min-width: 38em) {
    padding: 1rem 3rem;
    font-size: 1rem;
    margin-left: 1rem;
    margin-right: 1rem;
  }
  @media screen and (min-width: 72em) {
    margin: 0;
  }
`;

const QuizInner = styled(Box)`
  background: #f6f4f4;
  padding-top: 1rem;
  margin-bottom: 0rem;
  @media screen and (min-width: 72em) {
    padding-top: 5rem;
  }
`;


export const Quiz = props => {
  const user = getUser();
  const locale = currentLocale();
  const userLocale = currentUserLocale();
  const matchingLocale = userLocale === locale;


  const languageId = mapLocaleToLanguage[userLocale] || "en_US";

  const LoginPromt = (
    <LoginWarning>
      <SmallContainer>
        <LoginWarningBox
          display="flex"
          n
          justifyContent="space-between"
          alignItems="center"
        >
          <TextF6 m={0}>
            <FormattedMessage id="exercise.signUp" />:
          </TextF6>

          <Location>
            {({ location }) => {
              return (
                <Box display="flex">
                  <ButtonLogin
                    to={`${props.intl.formatMessage({
                      id: "routes.SIGN_IN"
                    })}?${location.pathname}`}
                  >
                    <FormattedMessage id="globals.logIn" />
                  </ButtonLogin>
                  <ButtonSignUp
                    to={`${props.intl.formatMessage({
                      id: "routes.SIGN_UP"
                    })}?${location.pathname}`}
                  >
                    <FormattedMessage id="globals.signUp" />
                  </ButtonSignUp>
                </Box>
              );
            }}
          </Location>
        </LoginWarningBox>
      </SmallContainer>
    </LoginWarning>
  );

  const WrongLocale = (
    <LoginWarning>
      <SmallContainer>
        <LoginWarningBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <TextF6 m={0}>
            <WrongLocaleMessage
              userLocale={userLocale}
              currentLocale={locale}
            />
          </TextF6>
        </LoginWarningBox>
      </SmallContainer>
    </LoginWarning>
  );

  const Loading = (
    <Wrapper>
      <QuizInner>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          pb={5}
        >
          <TextF6 textAlign="center">
            <FormattedMessage id="exercise.loading" />
          </TextF6>
          <BounceLoader />
        </Box>
      </QuizInner>
    </Wrapper>
  );

  const customContent = {
    Login: !user && LoginPromt,
    WrongLocale: user && !matchingLocale && WrongLocale,
    Loading: Loading
  };

  return (
    <Quiz2
      id={props.quizid}
      languageId={languageId}
      accessToken={user && user.accessToken}
      backendAddress="https://quizzes.mooc.fi"
      customContent={customContent}
      fullInfoWithoutLogin={true}
    >
      {props.children}
    </Quiz2>
  );
};

export class QuizDepr extends React.Component {
  static contextTypes = {
    loggedIn: PropTypes.bool
  };

  isAnswered(quiz, answer) {
    if ([MULTIPLE_OPEN, RADIO_MATRIX].indexOf(quiz.type) >= 0) {
      return Object.keys(_get(answer, `data`) || {}).length > 0;
    }
    return !!_get(answer, `data`);
  }

  defaultAnswer(quiz) {
    if ([ESSAY, OPEN].indexOf(quiz.type) >= 0) {
      return "";
    }
    return [];
  }

  getQuizProperties() {
    const {
      quiz,
      answer,
      peerReviews: peerReviewsGivenReceived,
      rejectedAnswer,
      quizid,
      notifications
    } = this.props;

    // there's more than one prop not used here
    return {
      quiz: _get(quiz, "data"),
      answer: _get(answer, `data`) || this.defaultAnswer(_get(quiz, `data`)),
      rejectedAnswer: rejectedAnswer,
      peerReviewsGiven: _get(peerReviewsGivenReceived, `given`) || [],
      peerReviewsReceived: _get(peerReviewsGivenReceived, `received`) || [],
      notifications: _get(notifications, `[${quizid}]`) || [],
      changed: _get(answer, `changed`) || false,
      submitted: _get(answer, `submitted`) || false,
      confirmed: _get(answer, `confirmed`) || false,
      rejected: !!rejectedAnswer || false,
      submitting: _get(answer, `submitting`) || false,
      validating: _get(answer, `validating`) || false,
      onReceivePeerReview: this.props.receivePeerReview.bind(this),
      onEssayChange: this.props.onChange.bind(this),
      onMultipleChoiceChange: this.props.onChange.bind(this),
      onRadioMatrixChange:
        _get(quiz, `data.data.meta.multi`) || false
          ? this.props.onChangeMultiItem.bind(this)
          : this.props.onChangeItem.bind(this),
      onOpenQuizChange: this.props.onChange.bind(this),
      onMultipleOpenChange: this.props.onChangeItem.bind(this),
      onMultipleSelected: this.props.onChangeItem.bind(this),
      onSingleSelected: this.props.onChange.bind(this),
      onSubmit: this.props.submitAnswerValidate.bind(this),
      onSubmitPeerReview: this.props.onSubmitPeerReview.bind(this),
      validation: _get(answer, "validation") || {},
      //score: _get(answer, 'validation.score') || {}, // this.state.score,
      messages: _get(answer, "validation.messages") || {}, // this.state.messages,
      rightAnswer: _get(answer, "validation.rightAnswer", {}),
      submitMessage: _get(quiz, `data.data.meta.submitMessage`) || null,
      loggedIn: this.context.loggedIn,
      completed: !!_get(answer, "data") && quiz.submitted && quiz.confirmed, // TODO: confirmed?
      peerreviewid: this.props.peerreviewid,
      error: _get(answer, `error`)
      // this.state.completed,
    };
  }

  componentWillReceiveProps(nextProps) {
    /*
      component will mount before the quiz is loaded --
      normally this is not a problem, but if the quiz
      is not in the ones in tags, then it won't load
      unless we do it after the init
    */
    const { quiz, quizzes, quizid } = this.props;
    const newAnswers = nextProps.answers;
    const newQuizzes = nextProps.quizzes;

    const newQuiz = _get(newQuizzes, `[${quizid}]`) || null;
    const newAnswer = _get(newAnswers, `[${quizid}]`) || null;

    return new Promise((resolve, reject) => {
      // TODO: do I chuck this?
      if (
        quizzes.initializing &&
        !newQuizzes.initializing &&
        !quiz &&
        !newQuiz
      ) {
        return resolve(this._fetchQuiz());
      }

      return resolve(null);
    })
      .then(returned => {
        if (!newAnswer) {
          return;
        }
        // this is to validate if we've received quiz
        // without validation or just answered/reloaded/whatever

        // TODO: do I chuck this as well or change to validation.rightanswer?
        if (
          !!_get(newQuiz, "data.data.meta.rightAnswer", null) &&
          Object.keys(_get(newAnswer, "validation.messages", {})).length ===
          0 &&
          !newAnswer.validating &&
          newAnswer.submitted
        ) {
          // this.props.onValidateAnswerData(newAnswer)
        }
        return;
      })
      .catch(err => { });
  }

  componentDidMount() {
    const { answer } = this.props;

    if (!!answer && answer.changed && !answer.submitted) {
      return;
    }

    if (!!answer && answer.submitted) {
      // ...if already validated, set only messages?
      // this.props.onValidateAnswerData(answer)
    }
  }

  _fetchQuiz() {
    const { quiz } = this.props;

    return new Promise((resolve, reject) => {
      // forget earlier - TODO: is this kosher?
      if (
        !quiz ||
        (!!quiz && (!quiz.receivedAt || quiz.receivedAt < Date.now() - 30000))
      ) {
        return this.props.fetchQuiz().catch(err => reject(err));
      }
      return resolve({});
    });
  }

  render() {
    const {
      quiz,
      rejectedAnswer,
      quizzesInitializing,
      answer,
      intl
    } = this.props;

    // answer has loading and validating as well
    const loading = !quiz || quiz.loading || quizzesInitializing;
    const error = _get(quiz, "error");

    if (loading || error) {
      return (
        <Wrapper>
          <QuizInner>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              pb={5}
            >
              {!error ? (
                <div>
                  <TextF6 textAlign="center">
                    <FormattedMessage id="exercise.loading" />
                  </TextF6>
                  <BounceLoader />
                </div>
              ) : (
                <div>
                  <TextF6 textAlign="center" style={{ color: "#FF0000" }}>
                    Error: {error.message}
                  </TextF6>
                </div>
              )}
            </Box>
          </QuizInner>
        </Wrapper>
      );
    }

    if (!quiz.data) {
      return null; // no error?
    }

    const Component = mapQuizTypeToComponent[quiz.data.type];
    const userLocale = currentUserLocale();
    const matchingLocale = userLocale === intl.locale;

    return (
      <Wrapper>
        <QuizInner>
          <QuizHeader
            quizTitle={quiz.data.title}
            quizBody={quiz.data.body}
            error={_get(answer, `error`)}
            rejectedAnswer={rejectedAnswer}
            submitted={(!!answer && _get(answer, `submitted`)) || false}
            loggedIn={this.context.loggedIn}
            matchingLocale={matchingLocale}
            dynamicContent={this.props.children}
          />
          {!this.context.loggedIn ? (
            <LoginWarning>
              <SmallContainer>
                <LoginWarningBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <TextF6 m={0}>
                    <FormattedMessage id="exercise.signUp" />:
                  </TextF6>

                  <Location>
                    {({ location }) => {
                      return (
                        <Box display="flex">
                          <ButtonLogin
                            to={`${routes.SIGN_IN}?${location.pathname}`}
                          >
                            <FormattedMessage id="globals.logIn" />
                          </ButtonLogin>
                          <ButtonSignUp
                            to={`${routes.SIGN_UP}?${location.pathname}`}
                          >
                            <FormattedMessage id="globals.signUp" />
                          </ButtonSignUp>
                        </Box>
                      );
                    }}
                  </Location>
                </LoginWarningBox>
              </SmallContainer>
            </LoginWarning>
          ) : this.context.loggedIn && !matchingLocale ? (
            <LoginWarning>
              <SmallContainer>
                <LoginWarningBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <TextF6 m={0}>
                    <WrongLocaleMessage
                      userLocale={userLocale}
                      currentLocale={intl.locale}
                    />
                  </TextF6>
                </LoginWarningBox>
              </SmallContainer>
            </LoginWarning>
          ) : (
            <Box mt={5}>
              <Component {...this.props} {...this.getQuizProperties()} />
            </Box>
          )}
        </QuizInner>
      </Wrapper>
    );
  }
}

export default injectIntl(Quiz);
